import styles from "./ProgramDetails.module.scss";
import clsx from "clsx";
import { FormattedMessage } from "react-intl";
import useInclusiveAccessMaterialPrice from "./useInclusiveAccessMaterialPrice";
import OffScreen from "common/core-ui/OffScreen";
import { Action } from "../useMaterialActions";
import { ReactNode, useMemo } from "react";
import { MaterialAction } from "../MaterialActions";
import {
  CourseMaterial,
  useCoursesLoaderData,
  useProgramDetails,
} from "../coursesLoader";
import useFindCourse from "../useFindCourse";
import PrintDetails from "./PrintDetails";
import useAccessDuration from "../useAccessDuration";
import { getToday } from "common/utils/time";

export interface ProgramDetailsProps {
  actions: Action[];
  courseMaterial: CourseMaterial;
}

interface StatusDetails {
  title: ReactNode;
  explanation: ReactNode;
  showActions: Action[];
}

const ALWAYS_SHOWN_ACTIONS = [
  "view_order_history",
  "view_shipping",
  "update_shipping",
  "track_shipment",
];

export default function ProgramDetails(props: ProgramDetailsProps) {
  const { actions, courseMaterial } = props;
  const { courseId, materialStatus, materialFormat, trialStartDate } =
    courseMaterial;
  const course = useFindCourse({ courseId })!;
  const materialPrice = useInclusiveAccessMaterialPrice(courseMaterial);
  const { currentTerm } = useCoursesLoaderData();
  const {
    participating,
    name: programName,
    scope,
    userEffectiveProgram,
    ea: { allowsOptOut },
  } = useProgramDetails();
  const continuationMessageUnavailable = scope === "ea_only" && !allowsOptOut;
  const continuationShouldOmitNoChargeClaim =
    scope === "ea_only" || userEffectiveProgram === "equitable_access";

  const printInfo = currentTerm?.printInfo;
  const statusDetails = useMemo((): StatusDetails | null => {
    const showActions = actions.filter(
      (action) =>
        ALWAYS_SHOWN_ACTIONS.includes(action.type ?? "") ||
        action.key === "view_purchase_options"
    );
    if (materialStatus === "opted_in" && allowsOptOut) {
      const action = actions.find(
        (action: Action) => action.type === "opt_out"
      );

      const parsedTrialStartDate = trialStartDate
        ? new Date(trialStartDate)
        : null;
      const futureTrialStartDate =
        parsedTrialStartDate && parsedTrialStartDate > getToday()
          ? parsedTrialStartDate
          : null;

      return {
        title: (
          <div className={styles.green}>
            <FormattedMessage defaultMessage={"Opted In"} />
          </div>
        ),
        explanation: futureTrialStartDate ? (
          <FormattedMessage
            defaultMessage={
              "You are opted in. This item is included in your program and will be billed to your student account. \
                 You can access this item beginning on {trialStartDate, date, mm/dd/yyyy}."
            }
            values={{
              trialStartDate: futureTrialStartDate,
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage={
              "You are opted in. This item is included in your program and will be billed to your student account."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else if (materialStatus === "continuation") {
      if (continuationMessageUnavailable) {
        return null;
      }

      return {
        title: continuationShouldOmitNoChargeClaim ? (
          <FormattedMessage
            defaultMessage={
              "You still have access to this item from a previous class."
            }
          />
        ) : (
          <FormattedMessage
            defaultMessage={
              "You still have access to this item from a previous class and will not be charged again."
            }
          />
        ),
        explanation: <></>,
        showActions,
      };
    } else if (materialStatus === "opted_out" && allowsOptOut) {
      const action = actions.find((action: Action) => action.type === "opt_in");
      return {
        title: (
          <div className={styles.red}>
            <FormattedMessage defaultMessage={"Opted Out"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "You are opted out of this item. You need to purchase it on your own or opt back in."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else if (materialStatus === "included") {
      return {
        title: (
          <div className={styles.green}>
            <FormattedMessage defaultMessage={"Included - No Cost"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={"You have free access to this item."}
          />
        ),
        showActions,
      };
    } else if (materialStatus === "not_included") {
      const action = actions.find((action: Action) => action.type === "ePOS");
      return {
        title: (
          <div className={styles.red}>
            <FormattedMessage
              defaultMessage={"Not Included - Need to Purchase"}
            />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "This item is not included in your program and you need to purchase on your own."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else {
      return null;
    }
  }, [
    actions,
    materialStatus,
    allowsOptOut,
    continuationShouldOmitNoChargeClaim,
    continuationMessageUnavailable,
    trialStartDate,
  ]);
  const accessDuration = useAccessDuration(courseMaterial);

  return (
    <div className={styles.container}>
      <h3 className={styles.containerTitle}>
        <FormattedMessage defaultMessage="Program Details" />
      </h3>
      <dl
        data-testid="material-program-details"
        className={clsx(
          "vst-definition-list",
          "vst-definition-list-size-small"
        )}
      >
        {participating && (
          <>
            <dt data-testid="program-name">
              <FormattedMessage defaultMessage="Program" />
            </dt>
            <dd>{programName}</dd>
          </>
        )}
        {accessDuration && (
          <>
            <dt>
              <FormattedMessage defaultMessage="Access" />
            </dt>
            <dd>{accessDuration}</dd>
          </>
        )}

        <dt>
          <FormattedMessage defaultMessage="Course" />
        </dt>
        <dd>{course.title}</dd>

        {materialPrice && (
          <>
            <dt>
              <FormattedMessage defaultMessage="Price" />
            </dt>
            <dd>{materialPrice}</dd>
          </>
        )}

        {statusDetails && (
          <>
            <dt>
              <FormattedMessage defaultMessage="Status" />
            </dt>
            <dd>{statusDetails.title}</dd>
            <dt>
              <OffScreen>
                <FormattedMessage defaultMessage="Status Message" />
              </OffScreen>
            </dt>
            <dd>
              {materialFormat !== "print" && (
                <div
                  data-testid="status-explanation"
                  className={styles.statusMessage}
                >
                  {statusDetails.explanation}
                </div>
              )}
            </dd>
          </>
        )}

        {printInfo && (
          <PrintDetails courseMaterial={courseMaterial} printInfo={printInfo} />
        )}

        {statusDetails && statusDetails.showActions.length > 0 && (
          <>
            <dt>
              <OffScreen>
                <FormattedMessage defaultMessage="Material Actions" />
              </OffScreen>
            </dt>
            <dd>
              <div className={styles.actions}>
                {statusDetails.showActions.map((action) => (
                  <MaterialAction key={action.key} action={action} />
                ))}
              </div>
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}
